import { useEffect, useState } from 'react';
import { ArrowLeft, Boxes, Pencil, Save } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/Components/ui/Button';
import { Card } from '@/Components/ui/Card';
import { Input } from '@/Components/ui/Input';
import { Select } from '@/Components/ui/Select';
import { PageHeader } from '@/Components/data/PageHeader';
import DecimalInput from '@/Components/forms/DecimalInput';
import { Field } from '@/Components/forms/Field';
import FormStickyFooter from '@/Components/forms/FormStickyFooter';
import loteService, { type LoteInput, type LoteStatus } from '@/services/lote';
import produtoService, { type Produto } from '@/services/produto';
import { getUuidCandidate, isUuid } from '@/utils/uuid';

function gerarNumeroLote(): string {
  const agora = new Date();
  const y = agora.getFullYear();
  const m = String(agora.getMonth() + 1).padStart(2, '0');
  const d = String(agora.getDate()).padStart(2, '0');
  const rand = Math.floor(Math.random() * 9000 + 1000);
  return `L${y}${m}${d}${rand}`;
}

function hojeIso(): string {
  return new Date().toISOString().slice(0, 10);
}

const FORM_VAZIO: LoteInput = { numero_lote: gerarNumeroLote(), produto_id: '', quantidade: 0, data_fabricacao: hojeIso(), data_validade: '', status: 'LIVRE', localizacao: '', fornecedor: '' };

export default function LoteForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const visualizando = location.pathname.endsWith('/visualizar');
  const editando = Boolean(id) && !visualizando;
  const [form, setForm] = useState<LoteInput>(FORM_VAZIO);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [loading, setLoading] = useState(Boolean(id));
  const [salvando, setSalvando] = useState(false);
  const [erros, setErros] = useState<string[]>([]);
  const produtosValidos = produtos.filter((produto) => getUuidCandidate(produto));

  useEffect(() => { produtoService.listar().then(setProdutos).catch(console.error); }, []);
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    loteService.buscarPorId(id).then((data) => setForm({
      numero_lote: data.numero_lote,
      produto_id: data.produto_id,
      quantidade: data.quantidade,
      data_fabricacao: data.data_fabricacao || '',
      data_validade: data.data_validade || '',
      status: data.status,
      op_id: data.op_id || '',
      localizacao: data.localizacao || '',
      fornecedor: data.fornecedor || '',
    })).catch(console.error).finally(() => setLoading(false));
  }, [id]);

  const salvar = async () => {
    if (visualizando) return;

    const errosFormulario: string[] = [];
    if (!form.numero_lote?.trim()) errosFormulario.push('Número do lote: informe o número do lote.');
    if (!isUuid(form.produto_id)) errosFormulario.push('Produto: selecione um produto válido.');
    if (!form.quantidade || Number(form.quantidade) <= 0) errosFormulario.push('Quantidade: informe um valor maior que zero.');

    if (errosFormulario.length > 0) {
      setErros(errosFormulario);
      return;
    }

    setErros([]);
    setSalvando(true);
    try {
      if (id) await loteService.atualizar(id, form);
      else await loteService.criar(form);
      navigate('/producao/estoque/lotes');
    } catch (error) {
      console.error('Erro ao salvar lote', error);
      setErros(['Não foi possível salvar o lote. Verifique os dados e tente novamente.']);
    } finally {
      setSalvando(false);
    }
  };

  const title = visualizando ? 'Visualizar Lote' : editando ? 'Editar Lote' : 'Novo Lote';
  const description = visualizando ? 'Consulte produto, quantidade, validade e rastreabilidade do lote.' : 'Registre lote, produto, quantidade e validade.';

  return (
    <form className="nx-page nx-sngpc-form-page" noValidate onSubmit={(event) => { event.preventDefault(); void salvar(); }}>
      <PageHeader icon={<Boxes size={21} />} title={title} description={description} actions={<Button type="button" variant="ghost" onClick={() => navigate('/producao/estoque/lotes')}><ArrowLeft size={16} /> Voltar</Button>} />
      <Card className="nx-page-card nx-sngpc-form-card">
        {erros.length > 0 ? (
          <div className="nx-form-alert nx-form-alert--error" role="alert">
            <strong>Não foi possível salvar o lote.</strong>
            <ul>
              {erros.map((erro, index) => (
                <li key={`${erro}-${index}`}>{erro}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <fieldset disabled={loading || visualizando} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
        <section className="nx-sngpc-form-section">
          <div className="nx-sngpc-form-section__header"><h2>Dados do lote</h2></div>
          <div className="nx-sngpc-page-form-grid">
            <Field label="Número do lote *" htmlFor="lote-numero"><Input id="lote-numero" value={form.numero_lote} readOnly disabled={loading} required /></Field>
            <Field label="Produto *" htmlFor="lote-produto" className="nx-sngpc-field--full"><Select id="lote-produto" value={form.produto_id} onChange={(e) => setForm({ ...form, produto_id: e.target.value })} disabled={loading || visualizando || editando} required><option value="">Selecione</option>{produtosValidos.map((produto) => <option key={produto.id} value={getUuidCandidate(produto) || ''}>{produto.codigo} - {produto.descricao}</option>)}</Select></Field>
            <Field label="Quantidade *" htmlFor="lote-quantidade"><DecimalInput id="lote-quantidade" value={form.quantidade} onValueChange={(value) => setForm({ ...form, quantidade: value ?? 0 })} disabled={loading || visualizando} required /></Field>
            <Field label="Fabricação" htmlFor="lote-fabricacao"><Input id="lote-fabricacao" type="date" value={form.data_fabricacao || ''} onChange={(e) => setForm({ ...form, data_fabricacao: e.target.value })} disabled={loading || visualizando} /></Field>
            <Field label="Validade" htmlFor="lote-validade"><Input id="lote-validade" type="date" value={form.data_validade || ''} onChange={(e) => setForm({ ...form, data_validade: e.target.value })} disabled={loading || visualizando} /></Field>
            <Field label="Status" htmlFor="lote-status"><Select id="lote-status" value={form.status || 'LIVRE'} onChange={(e) => setForm({ ...form, status: e.target.value as LoteStatus })} disabled={loading || visualizando}><option value="LIVRE">Livre</option><option value="BLOQUEADO">Bloqueado</option><option value="QUARENTENA">Quarentena</option></Select></Field>
            <Field label="Localização" htmlFor="lote-localizacao"><Input id="lote-localizacao" value={form.localizacao || ''} onChange={(e) => setForm({ ...form, localizacao: e.target.value })} disabled={loading || visualizando} /></Field>
            <Field label="Fornecedor" htmlFor="lote-fornecedor"><Input id="lote-fornecedor" value={form.fornecedor || ''} onChange={(e) => setForm({ ...form, fornecedor: e.target.value })} disabled={loading || visualizando} /></Field>
          </div>
        </section>
        </fieldset>
      </Card>
      {visualizando ? (
        <FormStickyFooter>
          <div className="nx-form-footer-right" style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', flexWrap: 'wrap', marginLeft: 'auto' }}>
            <Button type="button" variant="ghost" onClick={() => navigate('/producao/estoque/lotes')}>
              <ArrowLeft size={16} />
              Voltar
            </Button>
            {id ? (
              <Button type="button" onClick={() => navigate(`/producao/estoque/lotes/${id}/editar`)}>
                <Pencil size={16} />
                Editar
              </Button>
            ) : null}
          </div>
        </FormStickyFooter>
      ) : (
        <FormStickyFooter cancelLabel="Cancelar" onCancel={() => navigate('/producao/estoque/lotes')} saveLabel="Salvar" loading={salvando} saveIcon={<Save size={16} />} />
      )}
    </form>
  );
}
